import _findIndex from 'lodash/findIndex';
import _map from 'lodash/map';
import _isNil from 'lodash/isNil';
import _find from 'lodash/find';

import {
  CalculatorState,
  CalculatorStoreAction, LayerState,
  LoadHeightState,
  PalletDataState,
  UnitDimensionsState,
  UnitWeightState,
} from 'interfaces/store';
import {
  CalculatorActionType,
  CalculatorStep,
  CalculatorStepState,
  LayerConfiguration,
  LoadHeightType,
  PalletType,
  ResultsUnitSystem,
  UnitDimension,
  UnitPackaging,
  UnitWeight,
} from 'enums/calculator';
import { CalculatorStateStep } from 'interfaces/calculator';
import {
  validateDimensions,
  validateHeight, validateOverlap,
  validatePalletData,
  validateWeight,
} from 'validators/calculator.validator';
import {
  calculateGridSize,
  CalculatorOptions,
  getDimensions,
  shouldRotateLoad,
} from 'utils/calculator';
import PatternId from 'enums/patterns';
import layerDefaultState from '../defaults/layer.defaults';

const defaultSteps: CalculatorStateStep[] = [
  {
    id: CalculatorStep.UNIT_INFORMATION,
    title: 'Unit information',
    state: CalculatorStepState.ACTIVE,
  },
  {
    id: CalculatorStep.PALLET_DATA,
    title: 'Pallet data',
  },
  {
    id: CalculatorStep.LAYER_CONFIGURATION,
    title: 'Layer configuration',
  },
  {
    id: CalculatorStep.LAYER_ALIGNMENT,
    title: 'Layer alignment',
  },
  {
    id: CalculatorStep.RESULTS,
    title: 'Results',
  },
];

const defaultDimensions: UnitDimensionsState = {
  type: UnitDimension.INCH,
  width: null,
  height: null,
  length: null,
};

const defaultWeight: UnitWeightState = {
  type: UnitWeight.LBS,
  value: null,
};

const defaultPallet: PalletDataState = {
  type: PalletType.PALLET_40x48,
  width: 40,
  length: 48,
  height: 6,
  unitType: UnitDimension.INCH,
};

const defaultLoadHeight: LoadHeightState = {
  type: LoadHeightType.LAYERS,
  value: null,
  unit: UnitDimension.INCH,
  layers: 1,
};

const initialState: CalculatorState = {
  steps: defaultSteps,
  currentStep: defaultSteps && defaultSteps[0],
  packagingType: UnitPackaging.BOX,
  dimensions: defaultDimensions,
  weight: defaultWeight,
  palletData: defaultPallet,
  layer: layerDefaultState,
  height: defaultLoadHeight,
  layerConfigType: LayerConfiguration.AUTO,
  resultsUnitSystem: ResultsUnitSystem.IMPERIAL,
  view3D: false,
};

const calculatorReducer = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: CalculatorState = initialState,
  action: CalculatorStoreAction,
) => {
  const {
    steps,
    currentStep,
    dimensions: currentDimensions,
    palletData: currentPalletData,
    resultsUnitSystem: currentResultsUnitSystem,
    height: currentHeight,
    layer,
    images = {},
  } = state;
  const {
    payload: {
      stepIndex,
      packagingType = UnitPackaging.BOX,
      dimensions,
      weight,
      palletData,
      patternId,
      patternType,
      units,
      height,
      layerConfigType,
      resultsUnitSystem,
      view3D,
      image,
      layerAlignmentItem,
      horizontalOverlap,
      verticalOverlap,
    } = {},
  } = action;

  switch (action.type) {
    case CalculatorActionType.SET_PALLET_DATA:
      return { ...state, palletData: validatePalletData(palletData as PalletDataState) };
    case CalculatorActionType.SET_UNIT_PACKAGING:
      return {
        ...state,
        packagingType,
        dimensions: validateDimensions(
          state.dimensions as UnitDimensionsState,
          packagingType,
        ),
        weight: validateWeight(state.weight as UnitWeightState, packagingType),
        layer: {
          ...layer,
          horizontalOverlap: 0,
          verticalOverlap: 0,
        },
      };
    case CalculatorActionType.SET_DIMENSIONS: {
      const { type = UnitDimension.MM } = dimensions || {};
      let newResultsUnitSystem = currentResultsUnitSystem;
      const newHeight = currentHeight;
      newHeight.unit = type;
      if (type === UnitDimension.INCH) {
        newResultsUnitSystem = ResultsUnitSystem.IMPERIAL;
      } else {
        newResultsUnitSystem = ResultsUnitSystem.METRIC;
      }
      return {
        ...state,
        resultsUnitSystem: newResultsUnitSystem,
        dimensions: validateDimensions(
          dimensions as UnitDimensionsState,
          state.packagingType,
        ),
        height: newHeight,
      };
    }
    case CalculatorActionType.SET_WEIGHT:
      return { ...state, weight: validateWeight(weight as UnitWeightState, state.packagingType) };
    case CalculatorActionType.SET_LOAD_HEIGHT:
      return {
        ...state,
        height: validateHeight(
          height as LoadHeightState,
          dimensions as UnitDimensionsState,
          state.palletData as PalletDataState,
        ),
      };
    case CalculatorActionType.VIEW_3D:
      return { ...state, view3D };
    case CalculatorActionType.NEXT_STEP: {
      const maxStepIndex = steps.length - 1;
      const currentStepIndex = _findIndex(steps, ({ id }) => currentStep.id === id);
      if (currentStepIndex !== -1) {
        const nextStepIndex = currentStepIndex + 1;
        if (nextStepIndex <= maxStepIndex) {
          const newSteps = _map(steps, (step, index) => {
            const stepState = index < nextStepIndex
              ? CalculatorStepState.COMPLETED
              : CalculatorStepState.INACTIVE;

            return {
              ...step,
              ...{ state: stepState },
            };
          });

          const newCurrentStep = newSteps[nextStepIndex];
          if (newCurrentStep) {
            newCurrentStep.state = CalculatorStepState.ACTIVE;
          }

          return {
            ...state,
            ...{ steps: newSteps, currentStep: newCurrentStep },
          };
        }
      }
      return state;
    }
    case CalculatorActionType.PREV_STEP: {
      const minStepIndex = 0;
      const currentStepIndex = _findIndex(steps, ({ id }) => currentStep.id === id);
      if (currentStepIndex !== -1) {
        const nextStepIndex = currentStepIndex - 1;
        if (nextStepIndex >= minStepIndex) {
          const newSteps = _map(steps, (step, index) => {
            const newState = index < nextStepIndex
              ? CalculatorStepState.COMPLETED
              : CalculatorStepState.INACTIVE;
            return {
              ...step,
              ...{ state: newState },
            };
          });

          const newCurrentStep = newSteps[nextStepIndex];
          if (newCurrentStep) {
            newCurrentStep.state = CalculatorStepState.ACTIVE;
          }

          return {
            ...state,
            ...{ steps: newSteps, currentStep: newCurrentStep },
          };
        }
      }
      return state;
    }
    case CalculatorActionType.JUMP_TO_STEP: {
      if (!_isNil(stepIndex)) {
        const newSteps = _map(steps, (step, index) => {
          const newState = index < stepIndex
            ? CalculatorStepState.COMPLETED
            : CalculatorStepState.INACTIVE;
          return {
            ...step,
            ...{ state: newState },
          };
        });
        const newStep = newSteps[stepIndex];
        if (newStep) {
          newStep.state = CalculatorStepState.ACTIVE;

          return {
            ...state,
            ...{ steps: newSteps, currentStep: newStep },
          };
        }
      }
      return state;
    }
    case CalculatorActionType.LAYER_SET_ACTIVE_PATTERN: {
      const { pattern: { items } } = layer;
      const activePattern = _find(items, { label: patternId });
      const {
        width: packageWidth,
        length: packageLength,
        type: packageDimensionType,
      } = currentDimensions;
      const {
        width: palletWidth,
        length: palletLength,
        unitType: palletUnitType,
      } = currentPalletData;
      const {
        units: unitsPerLayer,
        pattern: { type: alignmentType },
      } = layer;
      const options: CalculatorOptions = {
        unitsPerLayer,
        packageLength,
        packageDimensionType,
        packageWidth,
        palletLength,
        palletUnitType,
        palletWidth,
        alignmentType,
        horizontalOverlap: horizontalOverlap || 0,
        verticalOverlap: verticalOverlap || 0,
      };
      const arrangementDimensions = getDimensions(options);
      if (activePattern) {
        if (patternId === PatternId.GRID) {
          const {
            cols,
            rows,
            rotatedPackage,
          } = calculateGridSize(arrangementDimensions);
          activePattern.cols = cols;
          activePattern.rows = rows;
          activePattern.rotatedPackage = rotatedPackage;
        }
        activePattern.rotatedLoad = shouldRotateLoad(activePattern, options);
      }

      layer.pattern.activeItem = activePattern || null;
      return {
        ...state,
        layer,
      };
    }
    case CalculatorActionType.LAYER_SET_PATTERN_TYPE: {
      const { type } = layer.pattern || {};
      if (patternType && type !== patternType) {
        layer.pattern.type = patternType;

        return {
          ...state,
          layer,
        };
      }
      return state;
    }
    case CalculatorActionType.LAYER_SET_UNITS: {
      layer.units = Number(units) ? Math.round(Number(units)) : null;
      return { ...state, layer };
    }
    case CalculatorActionType.LAYER_SET_HORIZONTAL_OVERLAP: {
      layer.horizontalOverlap = Math.round(Number(horizontalOverlap));
      return {
        ...state,
        layer: validateOverlap(
          layer as LayerState,
          state.dimensions.type,
        ),
      };
    }
    case CalculatorActionType.LAYER_SET_VERTICAL_OVERLAP: {
      layer.verticalOverlap = Math.round(Number(verticalOverlap));
      return {
        ...state,
        layer: validateOverlap(
          layer as LayerState,
          state.dimensions.type,
        ),
      };
    }
    case CalculatorActionType.SET_LAYER_CONFIG_TYPE:
      return { ...state, layerConfigType };
    case CalculatorActionType.SET_RESULTS_UNIT_SYSTEM:
      return { ...state, resultsUnitSystem };
    case CalculatorActionType.SET_2D_IMAGE:
      images.i2d = image;
      return { ...state, images };
    case CalculatorActionType.SET_3D_IMAGE:
      images.i3d = image;
      return { ...state, images };
    case CalculatorActionType.SET_LAYER_ALIGNMENT_ITEM:
      if (layerAlignmentItem) {
        layer.alignmentItems = _map(layer.alignmentItems, (item) => {
          item.selected = item.id === layerAlignmentItem.id;
          return item;
        });
        return { ...state, layer };
      }
      return state;
    default:
      return state;
  }
};

export default calculatorReducer;
